import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  Link,
  useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, extractError, setAccessToken, tokenSalvo } from '@/lib/api.js';
import { AcessosDeSuporte, FaixaSuporte, SuporteEncerradoPage, SuporteEntrarPage } from '@/pages/SuportePages.js';
import { DiagnosticoFlow } from '@/features/cinco-forcas/DiagnosticoFlow.js';
import { FORCAS_DIAGNOSTICO } from '@/features/cinco-forcas/index.js';
import { RadarChart } from '@/features/cinco-forcas/radar.js';
import { downloadDiagnosticoPdf } from '@/features/cinco-forcas/pdf.js';
import { ScoreCard } from '@/features/cinco-forcas/ScoreCard.js';
import { DetalheForca } from '@/features/cinco-forcas/DetalheForca.js';
import { EvolutionChart, type EvolutionPoint } from '@/features/cinco-forcas/EvolutionChart.js';
import { InviteByEmail } from '@/features/cinco-forcas/InviteByEmail.js';
import { ComparePage } from '@/pages/ComparePage.js';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage.js';
import { ConfiguracoesPage } from '@/pages/ConfiguracoesPage.js';
import { AprendizadoPage } from '@/pages/AprendizadoPage.js';
import { TrilhaDetailPage } from '@/pages/TrilhaDetailPage.js';
import { AulaPlayerPage } from '@/pages/AulaPlayerPage.js';
import { CertificadoPublicPage } from '@/pages/CertificadoPublicPage.js';
import { LeadMagnetPage } from '@/pages/LeadMagnetPage.js';
import { ChatAssistPage } from '@/pages/ChatAssistPage.js';
import { EventosPage } from '@/pages/EventosPage.js';
import { MarketingLandingPage } from '@/pages/MarketingLandingPage.js';
import { LoginPage } from '@/pages/LoginPage.js';
import { MetodoDiagnosticoPage } from '@/pages/MetodoDiagnosticoPage.js';
import { BibliotecaPage } from '@/pages/BibliotecaPage.js';
import { BibliotecaDoMetodoPage } from '@/pages/BibliotecaDoMetodoPage.js';
import { CertificacaoPage, ProvaPage } from '@/pages/CertificacaoPage.js';
import { JornadasPage, MicroLearningPage } from '@/pages/ConhecimentoTrilhasPage.js';
import { ConselheiroPage } from '@/pages/ConselheiroPage.js';
import { ConselheiroEmpresaPage } from '@/pages/ConselheiroEmpresaPage.js';
import { ConselheiroManhaPage } from '@/pages/ConselheiroManhaPage.js';
import { ConselheiroResponderPage } from '@/pages/ConselheiroResponderPage.js';
import { AjudaArtigoPage, AjudaPage } from '@/pages/AjudaPage.js';
import { podeVer, useMe, type AreaRestrita } from '@/lib/me.js';
import { EcossistemaPage } from '@/pages/EcossistemaPage.js';
import { PainelPage } from '@/pages/PainelPage.js';
import { WorkspaceProvider } from '@/context/WorkspaceContext.js';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { FieldTip } from '@/components/FieldTip.js';
import { AppSidebar } from '@/components/AppSidebar.js';
import { AppTopBar } from '@/components/AppTopBar.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { PublicAuthShell } from '@/components/layout/PublicAuthShell.js';
import { ChatAssistFab } from '@/components/ChatAssistFab.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import type { AcademyRecursos } from '@shared/entitlements.js';

function ProtectedLayout() {
  const loc = useLocation();
  const token = tokenSalvo();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () =>
      (await api.get('/auth/me')).data as {
        id: string;
        role: string;
        recursos?: AcademyRecursos;
      },
    enabled: !!token,
  });
  if (!token) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  const isAdmin = me.data?.role === 'ADMIN' || me.data?.role === 'SUPER_ADMIN';
  const showConfig = isAdmin && import.meta.env.DEV;
  return (
    <WorkspaceProvider>
      <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
        <FaixaSuporte />
        <AppSidebar />
        <div className="md:ml-[240px] flex min-h-screen flex-col">
          <AppTopBar showConfig={showConfig} />
          <main className="flex-1 px-4 pb-6 pt-[72px] md:px-6 md:pt-6">
            <Outlet />
            <ChatAssistFab recursos={me.data?.recursos} />
          </main>
        </div>
      </div>
    </WorkspaceProvider>
  );
}

/**
 * Rota de área restrita: quem não tem acesso volta ao Início em vez de ver
 * uma tela que só daria recusa. A API recusa do mesmo jeito; isto é a porta.
 */
function Restrita({ area, children }: { area: AreaRestrita; children: ReactNode }) {
  const me = useMe();
  if (me.isLoading) return null;
  if (!podeVer(me.data, area)) return <Navigate to="/inicio" replace />;
  return <>{children}</>;
}

function EmpresaRedirect() {
  return <Navigate to="/metodo/diagnostico" replace />;
}

function ProtectedNotFound() {
  return <Navigate to="/inicio" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<MarketingLandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/suporte/entrar" element={<SuporteEntrarPage />} />
      <Route path="/suporte/encerrado" element={<SuporteEncerradoPage />} />
      <Route path="/entrar" element={<Navigate to="/login" replace />} />
      <Route path="/cadastro" element={<Cadastro />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/d/:codigo" element={<DiagnosticoPublic />} />
      <Route path="/ferramenta" element={<LeadMagnetPage />} />
      <Route path="/certificado/:codigo" element={<CertificadoPublicPage />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/inicio" element={<PainelPage />} />
        <Route path="/metodo/diagnostico" element={<MetodoDiagnosticoPage />} />
        <Route path="/metodo/biblioteca" element={<BibliotecaPage />} />
        <Route path="/conhecimento/jornadas" element={<JornadasPage />} />
        <Route path="/conhecimento/micro" element={<MicroLearningPage />} />
        <Route path="/direcao/mentorias" element={<Navigate to="/direcao/conselheiro" replace />} />
        <Route path="/direcao/conselheiro" element={<Restrita area="conselheiro"><ConselheiroPage /></Restrita>} />
        <Route path="/direcao/conselheiro/empresa/:id" element={<Restrita area="conselheiro"><ConselheiroEmpresaPage /></Restrita>} />
        <Route path="/direcao/conselheiro/manha/:id" element={<Restrita area="conselheiro"><ConselheiroManhaPage /></Restrita>} />
        <Route path="/direcao/conselheiro/radar/:radarId" element={<Restrita area="conselheiro"><ConselheiroResponderPage /></Restrita>} />
        <Route path="/direcao/biblioteca-do-metodo" element={<Restrita area="formacao"><BibliotecaDoMetodoPage /></Restrita>} />
        <Route path="/direcao/certificacao" element={<Restrita area="formacao"><CertificacaoPage /></Restrita>} />
        <Route path="/direcao/certificacao/prova/:id" element={<Restrita area="formacao"><ProvaPage /></Restrita>} />
        <Route path="/direcao/conselho" element={<Navigate to="/direcao/conselheiro" replace />} />
        <Route path="/tecnologia/hack-ia" element={<Restrita area="ia"><ChatAssistPage /></Restrita>} />
        <Route path="/conexao/ecossistema" element={<EcossistemaPage />} />
        <Route path="/dashboard" element={<Navigate to="/inicio" replace />} />
        <Route path="/aprendizado" element={<Navigate to="/conhecimento/jornadas" replace />} />
        <Route path="/mentoring" element={<Navigate to="/direcao/conselheiro" replace />} />
        <Route path="/assistente" element={<Navigate to="/tecnologia/hack-ia" replace />} />
        <Route path="/empresa/:id" element={<EmpresaRedirect />} />
        <Route path="/empresa/:id/diagnostico/novo" element={<DiagnosticoNovo />} />
        <Route path="/diagnostico/:id" element={<DiagnosticoResultado />} />
        <Route path="/diagnostico/:id/comparar" element={<ComparePage />} />
        <Route path="/conta" element={<Conta />} />
        <Route path="/ajuda" element={<AjudaPage />} />
        <Route path="/ajuda/:slug" element={<AjudaArtigoPage />} />
        <Route path="/configuracoes" element={<ConfiguracoesPage />} />
        <Route path="/aprendizado/trilha/:trilhaSlug" element={<TrilhaDetailPage />} />
        <Route path="/aprendizado/aula/:aulaId" element={<AulaPlayerPage />} />
        <Route path="/mentoring/*" element={<Navigate to="/direcao/conselheiro" replace />} />
        <Route path="/direcao/mentorias/*" element={<Navigate to="/direcao/conselheiro" replace />} />
        <Route path="/eventos" element={<EventosPage />} />
        <Route path="*" element={<ProtectedNotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}


const cadastroSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(8),
});

function Cadastro() {
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof cadastroSchema>>({
    resolver: zodResolver(cadastroSchema),
  });
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (data) => {
    setErr(null);
    try {
      const r = await api.post<{ accessToken: string }>('/auth/cadastro', {
        ...data,
        email: data.email.trim().toLowerCase(),
      });
      setAccessToken(r.data.accessToken);
      localStorage.setItem('ha_access', r.data.accessToken);
      nav('/inicio');
    } catch (e) {
      setErr(extractError(e).message);
    }
  });

  return (
    <PublicAuthShell title="Cadastro" subtitle="Crie sua conta Hack Academy" backTo="/login">
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <FieldTip label="Nome" hint="Nome completo para identificação nos diagnósticos e convites.">
            <Input {...register('name')} />
          </FieldTip>
          {errors.name && (
            <p className="text-xs" style={{ color: 'var(--danger)' }}>
              {errors.name.message}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FieldTip label="E-mail" hint="Será o login. Use um e-mail que acede regularmente.">
            <Input type="email" {...register('email')} />
          </FieldTip>
        </div>
        <div className="flex flex-col gap-2">
          <FieldTip label="Senha" hint="Mínimo 8 caracteres. Guarde em gestor de passwords se possível.">
            <Input type="password" {...register('password')} />
          </FieldTip>
        </div>
        {err && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {err}
          </p>
        )}
        <Button type="submit" style={{ width: '100%', padding: '12px 16px' }}>
          Criar conta
        </Button>
      </form>
    </PublicAuthShell>
  );
}

function Dashboard() {
  const q = useQuery({
    queryKey: ['empresas'],
    queryFn: async () => (await api.get('/empresas')).data as { items: { id: string; name: string }[] },
  });
  const nav = useNavigate();
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: async (payload: { name: string; segment?: string | null }) =>
      (await api.post('/empresas', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresas'] }),
  });
  const [name, setName] = useState('');
  const [segment, setSegment] = useState('');

  if (q.isLoading) return <PageLayout title="Empresas">Carregando…</PageLayout>;
  return (
    <PageLayout title="Empresas" description="Diagnósticos As 5 Forças por empresa ou projeto.">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1 space-y-3 md:flex md:gap-3 md:space-y-0">
          <div className="flex-1">
            <FieldTip label="Empresa" hint="Nome da empresa ou projeto que vai agrupar diagnósticos.">
              <input
                className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FieldTip>
          </div>
          <div className="flex-1">
            <FieldTip label="Segmento (opcional)" hint="Setor ou nicho — útil para relatórios futuros.">
              <input
                className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
                placeholder="Ex.: Indústria, serviços…"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              />
            </FieldTip>
          </div>
        </div>
        <button
          type="button"
          className="rounded-element px-4 py-2 text-xs font-bold uppercase text-black"
          style={{ background: 'var(--primary)' }}
          onClick={() =>
            name.trim() &&
            create.mutate({ name: name.trim(), segment: segment.trim() || null })
          }
        >
          Adicionar
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {q.data?.items.map((e) => (
          <button
            key={e.id}
            type="button"
            className="rounded-card border border-ha-border2 bg-ha-card p-4 text-left transition hover:border-accent"
            onClick={() => nav(`/empresa/${e.id}`)}
          >
            <div className="font-bold">{e.name}</div>
          </button>
        ))}
      </div>
    </PageLayout>
  );
}

function EmpresaPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [newDiagTitle, setNewDiagTitle] = useState('');
  const [editName, setEditName] = useState('');
  const [editSegment, setEditSegment] = useState('');

  const q = useQuery({
    queryKey: ['empresa', id],
    queryFn: async () =>
      (await api.get(`/empresas/${id}`)).data as {
        name: string;
        segment: string | null;
        meu_papel: string;
        membros: { id: string; papel: string; user: { id: string; name: string; email: string } }[];
        diagnosticos: {
          id: string;
          title: string | null;
          status: string;
          respostas_count: number;
        }[];
        evolution: EvolutionPoint[];
      },
    enabled: !!id,
  });

  useEffect(() => {
    if (!q.data) return;
    setEditName(q.data.name);
    setEditSegment(q.data.segment ?? '');
  }, [q.data?.name, q.data?.segment]);

  const patchEmpresa = useMutation({
    mutationFn: async (payload: { name: string; segment: string | null }) =>
      (await api.patch(`/empresas/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresa', id] }),
  });

  const deleteEmpresa = useMutation({
    mutationFn: async () => {
      await api.delete(`/empresas/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['empresas'] });
      nav('/inicio');
    },
  });

  const arquivarDiag = useMutation({
    mutationFn: async (diagId: string) => (await api.post(`/diagnosticos/${diagId}/arquivar`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresa', id] }),
  });

  if (!id || q.isLoading) return <p className="text-ha-muted">Carregando…</p>;
  const data = q.data!;
  const isDono = data.meu_papel === 'DONO';

  return (
    <div>
      <h1 className="mb-2 text-2xl font-black">{data.name}</h1>
      {data.segment && <p className="mb-4 text-sm text-ha-muted">Segmento: {data.segment}</p>}

      {isDono && (
        <div className="mb-6 max-w-xl space-y-3 border border-ha-border bg-ha-card p-4">
          <h2 className="text-xs font-bold uppercase text-ha-muted">Dados da empresa</h2>
          <FieldTip label="Nome" hint="Nome visível a todos os membros e nos convites.">
            <input
              className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </FieldTip>
          <FieldTip label="Segmento" hint="Opcional — ajuda a contextualizar relatórios.">
            <input
              className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
              value={editSegment}
              onChange={(e) => setEditSegment(e.target.value)}
              placeholder="Opcional"
            />
          </FieldTip>
          <button
            type="button"
            className="bg-ha-border px-4 py-2 text-xs font-bold"
            onClick={() =>
              editName.trim() &&
              patchEmpresa.mutate({ name: editName.trim(), segment: editSegment.trim() || null })
            }
          >
            Guardar alterações
          </button>
        </div>
      )}

      <EvolutionChart points={data.evolution} />

      {isDono && (
        <div className="mb-6 flex max-w-xl flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <FieldTip
              label="Título do novo diagnóstico (opcional)"
              hint="Ex.: Q1 2026 — aparece no histórico. Pode ficar em branco."
            >
              <input
                className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
                placeholder="Opcional"
                value={newDiagTitle}
                onChange={(e) => setNewDiagTitle(e.target.value)}
              />
            </FieldTip>
          </div>
          <button
            type="button"
            className="px-4 py-2 text-xs font-bold uppercase text-black"
            style={{ background: 'var(--primary)' }}
            onClick={() => {
              const t = newDiagTitle.trim();
              const qs = t ? `&t=${encodeURIComponent(t)}` : '';
              void nav(`/empresa/${id}/diagnostico/novo${qs}`);
            }}
          >
            Novo diagnóstico
          </button>
        </div>
      )}

      <h2 className="mb-2 text-sm font-bold uppercase text-ha-muted">Membros</h2>
      <ul className="mb-8 space-y-1 text-sm text-ha-subtle">
        {data.membros.map((m) => (
          <li key={m.id}>
            {m.user.name} ({m.user.email}) — {m.papel}
          </li>
        ))}
      </ul>

      <h2 className="mb-2 text-sm font-bold uppercase text-ha-muted">Histórico</h2>
      <ul className="space-y-2">
        {data.diagnosticos.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="text-left text-accent underline"
              onClick={() => nav(`/diagnostico/${d.id}`)}
            >
              {d.title || 'Diagnóstico'} — {d.respostas_count} respostas — {d.status}
            </button>
            {isDono && d.status === 'ATIVO' && (
              <AlertDialog.Root>
                <AlertDialog.Trigger asChild>
                  <button type="button" className="text-xs text-ha-muted underline">
                    Arquivar
                  </button>
                </AlertDialog.Trigger>
                <AlertDialog.Portal>
                  <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
                  <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(90vw,24rem)] -translate-x-1/2 -translate-y-1/2 border border-ha-border bg-ha-card p-4 shadow-none">
                    <AlertDialog.Title className="text-sm font-bold">Arquivar diagnóstico?</AlertDialog.Title>
                    <AlertDialog.Description className="mt-2 text-xs text-ha-subtle">
                      Deixa de aceitar novas respostas. Dados existentes mantêm-se.
                    </AlertDialog.Description>
                    <div className="mt-4 flex justify-end gap-2">
                      <AlertDialog.Cancel asChild>
                        <button type="button" className="px-3 py-1 text-xs">
                          Cancelar
                        </button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action asChild>
                        <button
                          type="button"
                          className="bg-accent px-3 py-1 text-xs font-bold text-black"
                          onClick={() => arquivarDiag.mutate(d.id)}
                        >
                          Arquivar
                        </button>
                      </AlertDialog.Action>
                    </div>
                  </AlertDialog.Content>
                </AlertDialog.Portal>
              </AlertDialog.Root>
            )}
          </li>
        ))}
      </ul>

      {isDono && (
        <div className="mt-10 border-t border-ha-border pt-6">
          <AlertDialog.Root>
            <AlertDialog.Trigger asChild>
              <button type="button" className="text-sm text-feedback underline">
                Apagar empresa
              </button>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
              <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
              <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(90vw,26rem)] -translate-x-1/2 -translate-y-1/2 border border-ha-border bg-ha-card p-4 shadow-none">
                <AlertDialog.Title className="text-sm font-bold">Apagar empresa?</AlertDialog.Title>
                <AlertDialog.Description className="mt-2 text-xs text-ha-subtle">
                  A empresa fica oculta da lista. Diagnósticos e respostas mantêm-se na base (soft delete).
                </AlertDialog.Description>
                <div className="mt-4 flex justify-end gap-2">
                  <AlertDialog.Cancel asChild>
                    <button type="button" className="px-3 py-1 text-xs">
                      Cancelar
                    </button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action asChild>
                    <button
                      type="button"
                      className="border border-feedback bg-ha-card2 px-3 py-1 text-xs font-bold text-feedback"
                      onClick={() => deleteEmpresa.mutate()}
                    >
                      Apagar
                    </button>
                  </AlertDialog.Action>
                </div>
              </AlertDialog.Content>
            </AlertDialog.Portal>
          </AlertDialog.Root>
        </div>
      )}
    </div>
  );
}

function DiagnosticoNovo() {
  const { id: empresaId } = useParams();
  const [sp] = useSearchParams();
  const diagId = sp.get('diag');
  const titleFromUrl = sp.get('t');
  const nav = useNavigate();
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const createDiag = async () => {
    if (!empresaId) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const r = await api.post(`/empresas/${empresaId}/diagnosticos`, {
        title: titleFromUrl?.trim() || null,
      });
      const did = (r.data as { id: string }).id;
      nav(`/empresa/${empresaId}/diagnostico/novo?diag=${did}`, { replace: true });
    } catch (e) {
      setCreateErr(extractError(e).message);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (!empresaId || diagId) return;
    void createDiag();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when empresaId set without diag
  }, [empresaId, diagId]);

  if (!empresaId) {
    return (
      <PageLayout title="Novo diagnóstico">
        <p className="text-sm text-ha-subtle">Empresa não indicada.</p>
        <button
          type="button"
          className="mt-4 text-sm text-accent underline"
          onClick={() => nav('/metodo/diagnostico')}
        >
          Voltar ao diagnóstico
        </button>
      </PageLayout>
    );
  }

  if (!diagId) {
    return (
      <PageLayout title="Novo diagnóstico">
        {creating && <p className="text-sm text-ha-subtle">A preparar diagnóstico…</p>}
        {createErr && (
          <div className="space-y-3">
            <p className="text-sm text-feedback">{createErr}</p>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-element px-4 py-2 text-xs font-bold uppercase text-black"
                style={{ background: 'var(--primary)' }}
                onClick={() => void createDiag()}
              >
                Tentar novamente
              </button>
              <button
                type="button"
                className="text-sm text-ha-subtle underline"
                onClick={() => nav('/metodo/diagnostico')}
              >
                Voltar
              </button>
            </div>
          </div>
        )}
      </PageLayout>
    );
  }

  return (
    <DiagnosticoFlow
      diagnosticoId={diagId}
      onCancel={() => nav('/metodo/diagnostico')}
      onComplete={async () => {
        nav(`/diagnostico/${diagId}`);
      }}
    />
  );
}

function DiagnosticoPublic() {
  const { codigo } = useParams();
  const nav = useNavigate();
  const q = useQuery({
    queryKey: ['pub', codigo],
    queryFn: async () =>
      (await api.get(`/d/${codigo}`)).data as { id: string; empresa: { name: string }; status: string },
    enabled: !!codigo,
  });

  const join = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ diagnostico_id: string | null }>(`/d/${codigo}/ingressar`);
      return r.data;
    },
    onSuccess: (data) => {
      if (data.diagnostico_id) nav(`/diagnostico/${data.diagnostico_id}`);
    },
  });

  if (!codigo || q.isLoading) return <p className="p-8">…</p>;
  if (!q.data) return <p className="p-8">Não encontrado</p>;
  const token = tokenSalvo();

  return (
    <div className="mx-auto max-w-lg p-8">
      <h1 className="mb-2 text-xl font-black">Diagnóstico</h1>
      <p className="mb-4 text-ha-subtle">Empresa: {q.data.empresa.name}</p>
      {!token ? (
        <button className="text-accent underline" type="button" onClick={() => nav(`/login?next=/d/${codigo}`)}>
          Entrar para responder
        </button>
      ) : (
        <button
          type="button"
          className="mt-4 px-6 py-3 font-bold text-black"
          style={{ background: 'var(--primary)' }}
          onClick={() => join.mutate()}
        >
          Ingressar e responder
        </button>
      )}
    </div>
  );
}

type RespostaRow = {
  user: { id: string; name: string; email: string };
  concluida: boolean;
  scores_json: { forcas: number[] } | null;
  respostas_json?: string;
};

function DiagnosticoResultado() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['diag', id],
    queryFn: async () =>
      (await api.get(`/diagnosticos/${id}`)).data as {
        codigo_convite: string;
        status: string;
        meu_papel: string | null;
        empresa_id: string;
        respostas: RespostaRow[];
      },
    enabled: !!id,
  });
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me')).data as { id: string },
  });

  const arquivar = useMutation({
    mutationFn: async () => (await api.post(`/diagnosticos/${id}/arquivar`)).data,
    onSuccess: () => {
      const cached = qc.getQueryData<{ empresa_id: string }>(['diag', id]);
      const eid = cached?.empresa_id;
      void qc.invalidateQueries({ queryKey: ['empresa', eid] });
      if (eid) nav('/metodo/diagnostico');
    },
  });

  const [flow, setFlow] = useState(false);

  const myResposta = useMemo(() => {
    if (!q.data || !me.data) return null;
    return q.data.respostas.find((r) => r.user.id === me.data!.id) ?? null;
  }, [q.data, me.data]);

  const isConcluida = myResposta?.concluida === true;
  const scoresFromServer = isConcluida ? (myResposta?.scores_json?.forcas ?? null) : null;
  const partialAnswers = useMemo(() => {
    if (!myResposta?.respostas_json) return null;
    try {
      return JSON.parse(myResposta.respostas_json) as (number | null)[];
    } catch {
      return null;
    }
  }, [myResposta?.respostas_json]);
  const flatFromServer = useMemo(() => {
    if (!partialAnswers || !isConcluida) return null;
    return partialAnswers as number[];
  }, [partialAnswers, isConcluida]);

  const concluidasCount = useMemo(
    () => (q.data?.respostas.filter((r) => r.concluida).length ?? 0),
    [q.data?.respostas],
  );

  useEffect(() => {
    if (q.data?.status === 'ARQUIVADO') setFlow(false);
  }, [q.data?.status]);

  if (!id || q.isLoading || me.isLoading) return <p className="p-8">…</p>;

  const d = q.data!;
  const isArquivado = d.status === 'ARQUIVADO';
  const podeResponder = !isArquivado;
  const isDonoDiag = d.meu_papel === 'DONO';

  if (flow && podeResponder) {
    return (
      <DiagnosticoFlow
        diagnosticoId={id!}
        initialAnswers={partialAnswers ?? undefined}
        onCancel={() => setFlow(false)}
        onComplete={async () => {
          setFlow(false);
          await q.refetch();
        }}
      />
    );
  }

  if (!scoresFromServer) {
    const hasPartial = partialAnswers?.some((a) => a !== null);
    return (
      <div className="p-8">
        <h1 className="mb-4 text-xl font-bold">Responder diagnóstico</h1>
        {isArquivado ? (
          <p className="text-sm text-ha-muted">Este diagnóstico está arquivado e já não aceita respostas.</p>
        ) : (
          <button
            type="button"
            className="px-6 py-3 font-bold text-black"
            style={{ background: 'var(--primary)' }}
            onClick={() => setFlow(true)}
          >
            {hasPartial ? 'Continuar diagnóstico' : 'Iniciar 50 perguntas'}
          </button>
        )}
      </div>
    );
  }

  const scores = scoresFromServer;
  const flat = flatFromServer ?? Array(50).fill(3);

  return (
    <div className="mx-auto max-w-3xl pb-16">
      <h1 className="mb-4 text-2xl font-black">Resultado</h1>
      {isArquivado && (
        <p className="mb-4 border border-ha-border bg-ha-card2 px-3 py-2 text-xs text-ha-muted">
          Diagnóstico arquivado — não é possível alterar respostas nem convidar novos participantes a responder.
        </p>
      )}
      <RadarChart scores={scores} />
      <div className="mb-6 grid grid-cols-5 gap-0.5 overflow-x-auto">
        {FORCAS_DIAGNOSTICO.map((f, i) => (
          <ScoreCard key={f.num} forca={f} score={scores[i]!} />
        ))}
      </div>
      <div className="mb-8 space-y-0">
        {FORCAS_DIAGNOSTICO.map((f, i) => (
          <DetalheForca key={f.num} forca={f} fi={i} score={scores[i]!} flat={flat} />
        ))}
      </div>
      {isDonoDiag && podeResponder && <InviteByEmail diagnosticoId={id!} />}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          className="border border-accent px-4 py-2 text-xs font-bold text-accent"
          onClick={() => void downloadDiagnosticoPdf(scores, flat)}
        >
          PDF
        </button>
        <button
          type="button"
          className="bg-ha-border px-4 py-2 text-xs font-bold"
          onClick={() => {
            void navigator.clipboard.writeText(`${window.location.origin}/d/${q.data?.codigo_convite}`);
          }}
        >
          Copiar link convite
        </button>
        {concluidasCount >= 2 && (
          <button type="button" className="text-xs underline" onClick={() => nav(`/diagnostico/${id}/comparar`)}>
            Comparar
          </button>
        )}
        {isDonoDiag && podeResponder && (
          <AlertDialog.Root>
            <AlertDialog.Trigger asChild>
              <button type="button" className="text-xs text-ha-muted underline">
                Arquivar diagnóstico
              </button>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
              <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
              <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(90vw,24rem)] -translate-x-1/2 -translate-y-1/2 border border-ha-border bg-ha-card p-4 shadow-none">
                <AlertDialog.Title className="text-sm font-bold">Arquivar este diagnóstico?</AlertDialog.Title>
                <AlertDialog.Description className="mt-2 text-xs text-ha-subtle">
                  O diagnóstico deixa de aceitar novas respostas. Os dados atuais mantêm-se.
                </AlertDialog.Description>
                <div className="mt-4 flex justify-end gap-2">
                  <AlertDialog.Cancel asChild>
                    <button type="button" className="px-3 py-1 text-xs">
                      Cancelar
                    </button>
                  </AlertDialog.Cancel>
                  <AlertDialog.Action asChild>
                    <button
                      type="button"
                      className="bg-accent px-3 py-1 text-xs font-bold text-black"
                      onClick={() => arquivar.mutate()}
                    >
                      Arquivar
                    </button>
                  </AlertDialog.Action>
                </div>
              </AlertDialog.Content>
            </AlertDialog.Portal>
          </AlertDialog.Root>
        )}
      </div>
      {podeResponder ? (
        <button type="button" className="text-sm text-ha-subtle underline" onClick={() => setFlow(true)}>
          Refazer respostas
        </button>
      ) : (
        <p className="text-sm text-ha-muted">Respostas bloqueadas (arquivado).</p>
      )}
    </div>
  );
}

function Conta() {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me')).data as { name: string; email: string },
  });
  const { register, handleSubmit } = useForm<{ name: string; password?: string }>();

  if (q.isLoading) return <PageLayout title="Conta">Carregando…</PageLayout>;

  return (
    <PageLayout title="Conta" description="Perfil e credenciais da sua conta.">
      <form
        onSubmit={handleSubmit(async (data) => {
          await api.patch('/auth/me', { name: data.name, password: data.password || undefined });
          await q.refetch();
        })}
        className="max-w-md space-y-3"
      >
        <FieldTip label="Nome" hint="Como aparece em diagnósticos e listas de membros.">
          <input
            key={q.data?.name}
            className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2"
            defaultValue={q.data?.name}
            {...register('name')}
          />
        </FieldTip>
        <FieldTip label="Nova senha" hint="Opcional — deixe em branco para manter a senha atual.">
          <input
            className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2"
            type="password"
            placeholder="Nova senha (opcional)"
            {...register('password')}
          />
        </FieldTip>
        <button type="submit" className="bg-accent px-4 py-2 text-xs font-bold text-black">
          Guardar
        </button>
      </form>
      <AcessosDeSuporte />
    </PageLayout>
  );
}
